import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { ArrowUpIcons } from '../../../../../../../icons/icons';
import Pagination from '../../../../../../../ui/components/Common/DataTable/Paginaion';

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  disableSort?: boolean;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface GridViewProps<T> {
  rowHeaders: ColumnDef<T>[];
  data: T[];
  columns: ColumnDef<T>[];
  sortConfig: SortConfig[];
  onSortChange: (key: string, dir: 'asc' | 'desc') => void;
  onItemClick: (item: T) => void;
  onDragEnd?: (items: T[]) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export const ResultsGridView = <T,>({
  rowHeaders,
  data,
  columns,
  sortConfig,
  onSortChange,
  onItemClick,
  onDragEnd,
  totalRecords,
  totalPages,
  rowsPerPage,
  onPageChange,
  currentPage,
}: GridViewProps<T>) => {
  const [items, setItems] = React.useState<T[]>(data);

  React.useEffect(() => {
    setItems(data);
  }, [data]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const updated = Array.from(items);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    setItems(updated);
    onDragEnd?.(updated);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 0.5 }}>
      {items.length === 0 ? (
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            minHeight: 180,
            alignItems: 'center',
            justifyContent: 'center',
            borderStyle: 'dashed',
            bgcolor: 'action.hover',
            p: 2,
            textAlign: 'center',
          }}
        >
          <Box>
            <Typography variant='subtitle2' sx={{ fontWeight: 600, color: 'text.primary' }}>
              No interview results yet
            </Typography>
            <Typography variant='caption' sx={{ mt: 0.5, maxWidth: 280, display: 'block', color: 'text.secondary' }}>
              Completed smart interview results will appear here.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              width: { xs: '100%', lg: 176 },
              overflowX: 'auto',
              borderRadius: 1,
              bgcolor: 'action.hover',
              p: 0.5,
              flexDirection: { lg: 'column' },
              overflow: { lg: 'visible' },
            }}
          >
            {rowHeaders.map((col) => {
              const entry = sortConfig.find((s) => s.key === col.key);
              const isAsc = entry?.direction === 'asc';
              const isDesc = entry?.direction === 'desc';
              const nextDir = isAsc ? 'desc' : 'asc';

              return (
                <Box
                  key={col.key}
                  onClick={() =>
                    !col.disableSort && onSortChange(col.key, nextDir)
                  }
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    height: 40,
                    minWidth: { xs: 140, lg: 0 },
                    cursor: col.disableSort ? 'default' : 'pointer',
                    userSelect: 'none',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    '&:hover .sort-icon': {
                      opacity: 1,
                    },
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                    }}
                  >
                    {col.label}
                  </Typography>

                  {!col.disableSort && (
                    <Box
                      className='sort-icon'
                      sx={{
                        position: 'absolute',
                        right: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        opacity: entry ? 1 : 0,
                        transition: 'opacity 0.15s',
                      }}
                    >
                      <ArrowUpIcons size={14} color={isAsc ? "#059669" : "#9ca3af"} style={{ transform: "rotate(90deg)" }} />
                      <ArrowUpIcons size={14} color={isDesc ? "#059669" : "#9ca3af"} style={{ transform: "rotate(-90deg)" }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ width: '100%', flex: 1, overflowX: 'auto', p: 0.5 }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='results' direction='horizontal'>
                {(prov) => (
                  <Box
                    ref={prov.innerRef}
                    {...prov.droppableProps}
                    sx={{ display: 'flex', gap: 1.5, pb: 1.5 }}
                  >
                    {items.map((item, idx) => (
                      <Draggable
                        key={idx}
                        draggableId={`result-${idx}`}
                        index={idx}
                      >
                        {(p) => (
                          <Paper
                            ref={p.innerRef}
                            {...p.draggableProps}
                            {...p.dragHandleProps}
                            variant='outlined'
                            onClick={() => onItemClick(item)}
                            sx={{
                              minWidth: 240,
                              flexShrink: 0,
                              cursor: 'pointer',
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              borderColor: 'divider',
                            }}
                          >
                            {columns.map((col) => {
                              const raw = (item as any)[col.key];
                              let num = NaN;
                              if (typeof raw === 'number') num = raw;
                              else if (raw?.score != null) num = raw.score;
                              let cellBg = 'transparent';
                              if (!isNaN(num)) {
                                if (num >= 85) cellBg = 'rgba(34, 197, 94, 0.08)';
                                else if (num >= 70) cellBg = 'rgba(59, 130, 246, 0.08)';
                                else if (num >= 50) cellBg = 'rgba(249, 115, 22, 0.08)';
                                else cellBg = 'rgba(239, 68, 68, 0.08)';
                              }
                              return (
                                <Box
                                  key={col.key}
                                  sx={{
                                    display: 'flex',
                                    height: 40,
                                    alignItems: 'center',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    px: 1.5,
                                    fontSize: '0.75rem',
                                    bgcolor: cellBg,
                                  }}
                                >
                                  {col.render
                                    ? col.render(item)
                                    : !isNaN(num)
                                      ? num
                                      : String(raw || '-')}
                                </Box>
                              );
                            })}
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {prov.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      )}

      {items.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Pagination
            totalRecords={totalRecords}
            currentPage={currentPage}
            handlePageChange={onPageChange}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            hasHidePagination={false}
          />
        </Box>
      )}
    </Box>
  );
};

export default ResultsGridView;
